import React, { ReactNode, forwardRef } from 'react';
import PFLink from '../PFLink';
import * as stylex from '@stylexjs/stylex';
import type { StyleXStyles } from '@stylexjs/stylex';
import '@/common/tokens.stylex';
import { colors } from '@/common/tokens.stylex';
import { stylexProps } from '@/util/stylexProps';
export type DeviderVariant = 'standard' | 'pinkSection' | 'bigDevider' | 'deviderWithHeader';
export type DeviderTagType = 'h1' | 'h2' | 'h3' | 'div';
type Props = {
  tagType?: DeviderTagType;
  variant?: DeviderVariant;
  title?: string;
  onlyMobile?: boolean;
  onlyDesktop?: boolean;
  highlight?: boolean;
  children?: React.ReactNode;
  biggerPaddingBottom?: boolean;
  noHorizontalPadding?: boolean;
  id?: string;
  secondaryTitle?: string;
  style?: StyleXStyles;
  className?: string;
  lightBorder?: boolean;
  uppercase?: boolean;
  href?: string;
  prefetch?: boolean | 'auto' | null;
  header?: ReactNode | undefined;
  noBorderDesktop?: boolean;
  textWhite?: boolean;
};
const Devider = forwardRef<HTMLDivElement, Props>((props: Props, ref) => {
  const {
    tagType = 'div',
    variant = 'standard',
    onlyMobile,
    onlyDesktop,
    highlight,
    title,
    children,
    biggerPaddingBottom,
    noHorizontalPadding,
    secondaryTitle,
    style,
    className,
    lightBorder,
    uppercase,
    href,
    prefetch,
    header = null,
    noBorderDesktop,
    textWhite
  } = props;
  const Tag = tagType;
  return <div ref={ref} {...stylexProps(className, styles.root, onlyMobile && styles.onlyMobile, onlyDesktop && styles.onlyDesktop, biggerPaddingBottom && styles.biggerPaddingBottom, !noHorizontalPadding && styles.defaultPad, noHorizontalPadding && styles.noHorizontalPadding, (variant === 'bigDevider' || variant === 'deviderWithHeader') && styles.px0, variant !== 'bigDevider' && !noHorizontalPadding && styles.px5, style)}>
      <div {...stylex.props(styles.row, variant !== 'deviderWithHeader' && styles.pb1, variant === 'standard' && !highlight && styles.borderLight, lightBorder && styles.lightBorder, variant === 'bigDevider' && styles.pbS, variant !== 'pinkSection' && styles.borderBottom, noBorderDesktop && styles.noBorderDesktop, highlight && styles.borderHighlight)}>
        <div {...{
        0: {
          className: "x78zum5 xdt5ytf x193iq5w x1iyjqo2 xs83m0k x1r8uery"
        },
        1: {
          className: "x17qtykl x78zum5 xdt5ytf x193iq5w x1iyjqo2 xs83m0k x1r8uery"
        }
      }[!!(variant === 'bigDevider') << 0]}>
          <Tag {...stylex.props(styles.title, (variant === 'bigDevider' || variant === 'deviderWithHeader') && styles.titleBig, variant !== 'bigDevider' && variant !== 'pinkSection' && styles.title18, variant === 'standard' && !highlight && styles.titleMuted, variant === 'pinkSection' && styles.titlePink, uppercase && styles.uppercase, !uppercase && styles.normalCase, highlight && styles.titleHighlight, textWhite && styles.textWhite)}>
            {!href && <>
                {title}
                {secondaryTitle && <span className="xfwgu6z xu0pfxz xo1l8bm x6mezaz x1120s5i xosj86m xt0psk2 x1l0h3s4 x1y0btm7 x1dx4pje xet2fuk x11iknt3 x1yrsyyn">{' '}{secondaryTitle}</span>}
              </>}
            {href && <PFLink href={href} prefetch={prefetch} mrfRecirculation="section-heading">
                {title}
                {secondaryTitle && <span className="xfwgu6z xu0pfxz xo1l8bm x6mezaz x1120s5i xosj86m xt0psk2 x1l0h3s4 x1y0btm7 x1dx4pje xet2fuk x11iknt3 x1yrsyyn">{' '}{secondaryTitle}</span>}
              </PFLink>}
          </Tag>
          {header}
        </div>
        {children}
      </div>
    </div>;
});
Devider.displayName = 'Devider';
const styles = {
  root: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  onlyMobile: {
    ko32ly: "x14imms7",
    $$css: true
  },
  onlyDesktop: {
    k1xSpc: "x1s85apg",
    ko32ly: "x1hgqwjb",
    $$css: true
  },
  biggerPaddingBottom: {
    kGO01o: "xx6bls6",
    $$css: true
  },
  defaultPad: {
    kLKAdn: "x889kno",
    kGO01o: "x18d9i69",
    k1K539: "xyorhqc",
    $$css: true
  },
  noHorizontalPadding: {
    k8WAf4: "xt970qd",
    kqGvvJ: "x10im51j",
    $$css: true
  },
  px0: {
    kg3NbH: "xnjsko4",
    $$css: true
  },
  px5: {
    kg3NbH: "x17qtykl",
    $$css: true
  },
  row: {
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np",
    kjj79g: "x1qughib",
    ksu8eU: "x1y0btm7",
    kOIVth: "x1jnr06f",
    $$css: true
  },
  pb1: {
    kGO01o: "xjkvuk6",
    $$css: true
  },
  pbS: {
    kGO01o: "xwib8y2",
    $$css: true
  },
  borderLight: {
    kVAM5u: "x1l0h3s4",
    $$css: true
  },
  lightBorder: {
    kVAM5u: "x1l0h3s4 xhoq0b8",
    $$css: true
  },
  borderBottom: {
    kt9PQ7: "x1co6499",
    $$css: true
  },
  noBorderDesktop: {
    kFagjx: "xn7ptaf",
    $$css: true
  },
  borderHighlight: {
    kVAM5u: "xjgz3kh x16ytxvd",
    $$css: true
  },
  title: {
    kGO01o: "xjkvuk6",
    k63SB2: "x1xlr1w8",
    k1xSpc: "xt0psk2",
    $$css: true
  },
  titleBig: {
    kMwMTN: "x6bodad",
    kGuDYH: "xneq65o",
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np",
    kwnvtZ: "x1a02dak",
    kGNEyG: "xuk3077",
    ko32ly: "x1hgqwjb",
    $$css: true
  },
  title18: {
    kGuDYH: "xosj86m",
    $$css: true
  },
  titleMuted: {
    kMwMTN: "xfwgu6z xu0pfxz",
    $$css: true
  },
  titlePink: {
    kMwMTN: "xxmjlet",
    kGuDYH: "xneq65o",
    $$css: true
  },
  uppercase: {
    kP9fke: "xtvhhri",
    $$css: true
  },
  normalCase: {
    kP9fke: "x6mezaz",
    $$css: true
  },
  titleHighlight: {
    kMwMTN: "x1ytf6ek x1qypnhb",
    $$css: true
  },
  textWhite: {
    kMwMTN: "xxmjlet",
    $$css: true
  }
};
export default Devider;